import { ActionTree } from "vuex";
import {
  R,
  S,
  SessionDetails,
  SessionsById,
  TerratalkLiveActionNames,
  TerratalkLiveActions,
  TerratalkLiveGetterNames,
  TerratalkLiveMutationNames
} from "./types";
import { api } from "@tt-app/api";
import { getAuthInfo } from "@tt-app/utils";
import { ROUTES, VUEX_ROOT_ACTION_NAMES } from "@tt-app/constants";
import router from "@tt-app/router";

export const terratalkLiveActions: ActionTree<S, R> & TerratalkLiveActions = {
  [VUEX_ROOT_ACTION_NAMES.LOGOUT_REQUEST]: {
    root: true,
    async handler({ commit }) {
      commit(TerratalkLiveMutationNames.LIVE_STATE, null);
      commit(TerratalkLiveMutationNames.PREVIOUS_FETCHER_ALIAS, null);
      commit(TerratalkLiveMutationNames.SESSIONS, null);
      commit(TerratalkLiveMutationNames.SESSION_DETAILS, null);
      commit(TerratalkLiveMutationNames.LIMITED_MODE_DETAILS, null);
    }
  },

  async [TerratalkLiveActionNames.HIDE_SESSION_ENDED_MODAL]({ commit }) {
    commit(TerratalkLiveMutationNames.HIDE_SESSION_ENDED_MODAL);
  },

  async [TerratalkLiveActionNames.HIDE_SESSION_STARTED_MODAL]({ commit }) {
    commit(TerratalkLiveMutationNames.HIDE_SESSION_STARTED_MODAL);
  },

  async [TerratalkLiveActionNames.REQUEST_LIMITED_MODE_DETAILS]({
    commit,
    dispatch,
    getters,
    rootGetters
  }) {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      const sessions: SessionsById = getters[TerratalkLiveGetterNames.SESSIONS];
      const sessionDetails: SessionDetails = getters[TerratalkLiveGetterNames.SESSION_DETAILS];

      const activeSession: SessionDetails = Object.values(sessionDetails || {}).filter(session => {
        const startTime = Date.parse(session.startsAt);
        const now = Date.now();

        return startTime < now;
      })?.[0];
      const activeLimitedMode = getters[TerratalkLiveGetterNames.LIMITED_MODE_DETAILS];
      const activeLimitedModeId = activeLimitedMode?.id;

      const sessionId = activeSession?.id;
      const limitedModeId = sessions?.[sessionId]?.limitedMode?.id;

      let payload = undefined;
      if (!sessionId || !limitedModeId) {
        console.log("no limited mode");
      } else {
        const { data } = await api.terratalkLive.limitedModeDetails({
          authInfo,
          limitedModeId,
          sessionId
        });

        if (activeLimitedModeId !== limitedModeId) {
          const currentPath = router.currentRoute;
          const inExercise = currentPath.path.includes("exercise");
          const inLobby = currentPath.name === ROUTES.TERRATALK_LIVE.LOBBY.NAME;

          if (!inExercise && !inLobby) {
            dispatch(TerratalkLiveActionNames.SHOW_SESSION_STARTED_MODAL);
          }

          router.push({
            name: ROUTES.TERRATALK_LIVE.LOBBY.NAME,
            params: {
              limitedMode: "true"
            }
          });
        }

        payload = data;
      }

      commit(TerratalkLiveMutationNames.LIMITED_MODE_DETAILS, payload);
    } catch (error) {
      console.log(`${TerratalkLiveActionNames.REQUEST_LIMITED_MODE_DETAILS} failed`, error);
    }
  },

  async [TerratalkLiveActionNames.REQUEST_STATE]({
    commit,
    dispatch,
    getters,
    rootGetters,
    rootState,
    state
  }) {
    const maintenanceMode = rootState.maintenanceMode;

    if (maintenanceMode.active) return;

    try {
      const authInfo = await getAuthInfo(rootGetters);
      const deviceUuid = rootState.deviceUuid;
      const activeSession = getters[TerratalkLiveGetterNames.ACTIVE_SESSION];

      const { data } = await api.terratalkLive.state({
        authInfo,
        currentSessionId: activeSession?.id,
        deviceUuid
      });

      const storedSessions = state.liveState?.sessions || [];
      const fetchedSessions = data.sessions;

      const previousFetcherAlias = state.previousFetcherAlias;
      const currentlySelectedAlias = (rootState as any).user?.selectedAlias;
      const aliasChanged = previousFetcherAlias?.uuid !== currentlySelectedAlias?.uuid;
      const noSession = storedSessions?.length > 0 && fetchedSessions?.length <= 0;

      const fetchedActiveSession = fetchedSessions?.find(fetched => {
        return fetched.id === activeSession?.id;
      });

      const limitedModeStarted =
        (!activeSession || activeSession.limitedMode === null) &&
        fetchedActiveSession?.limitedMode !== null;
      const limitedModeEnded =
        activeSession &&
        activeSession.limitedMode !== null &&
        fetchedActiveSession?.limitedMode === null;
      const limitedModeVersionChanged =
        activeSession &&
        activeSession.limitedMode?.version !== fetchedActiveSession?.limitedMode?.version;

      commit(TerratalkLiveMutationNames.PREVIOUS_FETCHER_ALIAS, currentlySelectedAlias);
      commit(TerratalkLiveMutationNames.LIVE_STATE, data);

      if (!aliasChanged && noSession) {
        dispatch(TerratalkLiveActionNames.SHOW_SESSION_ENDED_MODAL);
      }

      await dispatch(TerratalkLiveActionNames.REQUEST_SESSION_DETAILS);

      if (limitedModeStarted || limitedModeVersionChanged) {
        dispatch(TerratalkLiveActionNames.REQUEST_LIMITED_MODE_DETAILS);
      } else if (limitedModeEnded) {
        commit(TerratalkLiveMutationNames.LIMITED_MODE_DETAILS, undefined);
      }
    } catch (error) {
      console.log(`${TerratalkLiveActionNames.REQUEST_STATE} failed`, error);
    }
  },

  async [TerratalkLiveActionNames.REQUEST_SESSION_DETAILS]({
    commit,
    dispatch,
    rootGetters,
    state
  }) {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      const storedSessions = state.sessions;
      const fetchedSessions = state.liveState?.sessions;

      const sessionsById = fetchedSessions?.reduce((accumulator, session) => {
        return {
          ...accumulator,
          [session.id]: session
        };
      }, {});
      commit(TerratalkLiveMutationNames.SESSIONS, sessionsById);

      const updatedSessions = fetchedSessions?.filter(fetched => {
        const stored = storedSessions?.[fetched.id];

        const versionChanged = fetched.version !== stored?.version;
        const deviceChanged = fetched.activeDeviceUuid !== stored?.activeDeviceUuid;

        const limitedModeStarted = fetched.limitedMode !== null && stored?.limitedMode === null;
        const limitedModeEnded = fetched.limitedMode === null && stored?.limitedMode !== null;
        const limitedModeChanged = limitedModeStarted || limitedModeEnded;

        return versionChanged || deviceChanged || limitedModeChanged;
      });

      if (updatedSessions && updatedSessions?.length > 0) {
        const sessionDetailRequestPromises = updatedSessions.map(fetchedSession => {
          return api.terratalkLive.session({ authInfo, sessionId: fetchedSession.id });
        });

        const sessionDetailRequestResponses = await Promise.all(sessionDetailRequestPromises);

        const sessionDetailsBySessionId = sessionDetailRequestResponses.reduce(
          (accumulator, sessionDetailsResponse) => {
            if (!sessionDetailsResponse) return accumulator;

            const { data } = sessionDetailsResponse;

            return {
              ...accumulator,
              [data.id]: data
            };
          },
          {}
        );

        sessionDetailRequestResponses.forEach(response => {
          const sessionDetails = response.data;
          const startTime = Date.parse(sessionDetails.startsAt);
          const now = Date.now();

          const sessionStarted = startTime < now;
          const noDeviceId = sessionDetails.activeDeviceUuid === null;

          if (sessionStarted && noDeviceId) {
            dispatch(TerratalkLiveActionNames.TAKE_OWNERSHIP);
          }
        });

        commit(TerratalkLiveMutationNames.SESSION_DETAILS, sessionDetailsBySessionId);
      }

      if (!fetchedSessions?.length) {
        commit(TerratalkLiveMutationNames.SESSION_DETAILS, undefined);
      }
    } catch (error) {
      console.log(`${TerratalkLiveActionNames.REQUEST_SESSION_DETAILS} failed`, error);
    }
  },

  async [TerratalkLiveActionNames.SHOW_SESSION_ENDED_MODAL]({ commit }) {
    commit(TerratalkLiveMutationNames.SHOW_SESSION_ENDED_MODAL);
  },

  async [TerratalkLiveActionNames.SHOW_SESSION_STARTED_MODAL]({ commit }) {
    commit(TerratalkLiveMutationNames.SHOW_SESSION_STARTED_MODAL);
  },

  async [TerratalkLiveActionNames.TAKE_OWNERSHIP]({
    commit,
    dispatch,
    rootGetters,
    rootState,
    state
  }) {
    const authInfo = await getAuthInfo(rootGetters);
    const currentSessionId = state.liveState?.sessions?.[0]?.id;
    const deviceUuid = rootState.deviceUuid;

    const { data } = await api.terratalkLive.state({
      authInfo,
      currentSessionId,
      deviceUuid,
      force: 1
    });

    commit(TerratalkLiveMutationNames.LIVE_STATE, data);

    dispatch(TerratalkLiveActionNames.REQUEST_SESSION_DETAILS);
  }
};
